import ApiClient from '@/client';
import router from '@/router';
import { getPusherInstance } from '@/utils';

export const state = () => {
  return {
    loggingIn: false,
    loginError: null,
    loggedIn: false,
    loginSuccessful: false,
    userId: null,
    eventId: null,
    cookieInfo: null,
    user: null,
    defaultImage: 'https://ideffector-prod.s3.amazonaws.com/images/avatar.png',
    setPasswordSuccess: null,
    setPasswordError: null,
    settingPassword: false
  };
};

export const getters = {
  isLoggedIn: (state) => {
    return state.loggedIn;
  },
  isLoggingIn: (state) => {
    return state.loggingIn;
  },
  getUser: (state) => {
    return state.user;
  },
  getDefaultImage: (state) => {
    return state.defaultImage;
  },
  isSetPasswordSuccess: (state) => {
    return state.setPasswordSuccess;
  },
  isSetPasswordError: (state) => {
    return state.setPasswordError;
  }
};

export const mutations = {
  setDefaultImage: (state, payload) => {
    state.defaultImage = payload;
  },
  setUser: (state, payload) => {
    state.user = payload;
  },
  clearUser: (state) => {
    state.user = null;
  },
  setCookieInfo(state, payload) {
    state.cookieInfo = payload;
  },
  setLoggedIn: (state, payload) => {
    state.loggedIn = payload;
  },
  loginStart: (state) => {
    state.loggingIn = true;
  },
  loginStop: (state) => {
    state.loggingIn = false;
  },
  loginSuccess: (state, response) => {
    state.eventId = response.eventID;
    state.loggedIn = response.loginSuccessful;
    state.loginSuccessful = response.loginSuccessful;
    state.userId = response.userID;
    state.loginError = null;
  },
  loginFailure: (state, response) => {
    state.loggedIn = false;
    state.loginSuccessful = false;
    state.loginError = response.message;
  },
  Logout: (state) => {
    state.loggedIn = false;
    state.loggingIn = false;
    state.loginError = null;
    state.userId = false;
    state.loginSuccessful = null;
    state.eventId = null;
    state.user = null;
    state.cookieInfo = null;
  },
  setPasswordError: (state, response) => {
    state.setPasswordError = response;
  },
  setPasswordSuccess: (state, response) => {
    state.setPasswordSuccess = response;
  },
  setPasswordStart: (state) => {
    state.settingPassword = true;
  },
  setPasswordStop: (state) => {
    state.settingPassword = false;
  }
};

export const actions = {
  async doLogin({ dispatch, commit, rootGetters, rootState }, loginData) {
    await commit('loginStart');
    const email = loginData.email && loginData.email.trim();

    const response = await ApiClient.login(
      email,
      loginData.password,
      loginData.eventId,
      loginData.visible,
      loginData.pin,
      loginData.pinId,
      loginData.emailAndPhoneVisible
    ).catch(async () => {
      return { data: { loginSuccessful: false } };
    });

    if (response.data.loginSuccessful === false) {
      commit('loginFailure', response.data);
    } else {
      response.data.eventID = loginData.eventId;
      commit('loginSuccess', response.data);
      await dispatch('getUser');
    }

    await commit('loginStop');
  },

  async doPasswordlessLogin(
    { dispatch, commit, rootGetters, rootState },
    data
  ) {
    await commit('loginStart');

    const response = await ApiClient.passwordlessLogin(
      data.payload,
      data.eventId,
      data.visible,
      data.emailAndPhoneVisible
    ).catch(async () => {
      return { data: { loginSuccessful: false } };
    });

    if (response.data.loginSuccessful === false) {
      commit('loginFailure', response.data);
    } else {
      response.data.eventID = data.eventId;
      commit('loginSuccess', response.data);
      await dispatch('getUser');
    }

    await commit('loginStop');
  },

  async doSetPassword(
    { dispatch, commit, rootGetters, rootState },
    setPasswordData
  ) {
    await commit('setPasswordStart');

    const response = await ApiClient.setPassword(
      setPasswordData.eventId,
      setPasswordData.accessKey,
      setPasswordData.password
    ).catch(async () => {
      commit('setPasswordError', true);
    });

    commit('setPasswordError', false);
    commit('setPasswordSuccess', response.data);
    await commit('setPasswordStop');
  },

  async doLogout({ commit, dispatch, rootGetters }) {
    const eventId = rootGetters['CurrentEvent/getId'];
    // Drop this device's push subscription while the token is still valid
    // (the endpoint is authenticated). Otherwise the server keeps it under
    // this user and the device keeps receiving their private-message pushes
    // after logout, including when someone else uses the same browser.
    try {
      await dispatch('PushNotifications/disable', null, { root: true });
    } catch (e) {
      /* best-effort: never block logout */
    }
    localStorage.setItem(`tokens_${eventId}`, null);

    const websocketInstance = getPusherInstance();
    if (websocketInstance && websocketInstance.socket) {
      websocketInstance.socket.close(1000);
      clearInterval(websocketInstance.heartbeat);
      websocketInstance.heartbeat = null;
    }

    commit('Logout');

    router.push({ name: 'login' });
    // refresh page to reset vuex state
    router.go(0);
  },

  changeAvatar({ dispatch }, file) {
    return ApiClient.uploadProfileAvatar(file).then((response) => {
      dispatch('getUser');
      return response;
    });
  },

  // Upload an avatar captured from the camera (a base64 data URL).
  changeAvatarFromDataUrl({ dispatch }, dataUrl) {
    return ApiClient.uploadBase64ImageAsProfileAvatar(dataUrl).then(
      (response) => {
        dispatch('getUser');
        return response;
      }
    );
  },

  // Remove the avatar. No dedicated delete endpoint exists, so we post an
  // empty image to the upload endpoint — the Avatar component then falls
  // back to the user's initials.
  removeAvatar({ dispatch }) {
    return ApiClient.uploadBase64ImageAsProfileAvatar('').then((response) => {
      dispatch('getUser');
      return response;
    });
  },

  async toggleProfileVisibility({ dispatch }, file) {
    ApiClient.toggleProfileVisibility(file).then((response) => {
      dispatch('getUser');
    });
  },

  async toggleEmailAndPhoneVisibility({ dispatch }, file) {
    ApiClient.toggleEmailAndPhoneVisibility(file).then((response) => {
      dispatch('getUser');
    });
  },

  async updateUser({ dispatch }, userData) {
    await ApiClient.updateProfile(userData);
    await dispatch('getUser');
  },

  // Re-reads the profile without the logout-on-failure of `getUser`: used for background
  // refreshes (tab refocus), where a dropped request must not sign the participant out.
  async refreshUser({ commit, state }) {
    if (!state.user) return;

    try {
      const response = await ApiClient.getCurrentInfoUserFromSession();
      if (response.data.userModuleResult) {
        commit('setUser', response.data.userModuleResult);
      }
    } catch (err) {
      // keep the profile we already have
    }
  },

  async getUser({ commit, dispatch }) {
    let response;
    try {
      response = await ApiClient.getCurrentInfoUserFromSession();
    } catch (err) {
      commit('setLoggedIn', false);
      commit('clearUser');
      await dispatch('doLogout');
      return;
    }

    if (response.data.userModuleResult) {
      commit('setUser', response.data.userModuleResult);
      const { Firstname, Lastname, Email } = response.data.userModuleResult;

      try {
        window.LiveChatWidget.call(
          'set_customer_name',
          `${Firstname} ${Lastname}`
        );
        window.LiveChatWidget.call('set_customer_email', Email);
      } catch (e) {
        console.log('LiveChat widget threw exception', e);
      }

      commit('setLoggedIn', true);
    } else {
      commit('clearUser');
      commit('setLoggedIn', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
